/**
 * 工作台「舱富卡」共用外壳(2026-09-20 抽出)—— 内容群卡墙上每一张大卡的形制单源。
 *
 * ★为什么抽出来:老板 0920 反馈「漫剧工坊的卡片风格还没有跟其他的统一」。同一组里
 *   5 张 dept 富卡(主角图 / 积分标 / chips / 身份色)旁边站着一张轻量 CapabilityCard,
 *   一眼就能看出是另一种卡。要统一就得**共用同一段 DOM 与同一套 .wb-* 类**,而不是
 *   把两个组件的样式各自往中间凑(那是双源,下次改一处必漂)。
 * ★为什么不是「把漫剧塞进 DEPT_ENTRIES」:那张表的每一行都承诺一个本站 hub + route +
 *   creditRates 积分标。外链舱三样都没有(账号/积分都在 drama 站内,catalog billable=0),
 *   塞一条假条目进去骗组件,卡面就会长出一个本站根本不收的价。
 *
 * 形制(自上而下,六张卡逐格对齐):身份色顶条 / 图标瓦片 + 标题 + 副标 / 一句话简介 /
 * 场景 minis 带(可选)/ 能力 chips(可选)/ 卡脚(左:计费口径 · 右:CTA)。
 * 卡脚一律**贴底**(.wb-body-main 吃掉剩余高度)—— 富卡按内容多少长短不一,不贴底的话
 * 少一条 minis 带的卡会把卡脚吊在半空,那又成了「另一种卡」。
 */
import type { CSSProperties, KeyboardEvent, ReactNode } from "react";
import type { LucideIcon } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { identityVar } from "./workbenchCatalog";

export interface CabinShellProps {
  /**
   * 结构标记:卡墙上**所有**富卡都带 `data-cabin-card`,走同一个外壳的证据就是它。
   * 各自原有的 data 属性(dept 卡 `data-dept-card` / 外链舱 `data-capability-card`)由
   * `legacyAttrName` 原样保留 —— 既有查询点(测试 / e2e)不因为换外壳而断。
   */
  cabinKey: string;
  legacyAttrName?: string;
  /** 入场 stagger 的序号(喂 `--i`)。 */
  index: number;
  /** 身份色档位(`--chart-N`);同一面墙上不许两张卡撞色。 */
  chart: number;
  icon: LucideIcon;
  title: string;
  /** 副标:dept 卡是「AI海报 · 14 个场景」,外链舱是「连载漫剧」+ 独立站标。 */
  tagline: ReactNode;
  desc: string;
  /** 场景样例标签带(外链舱没有本站场景,不画 —— 不编假样例)。 */
  minis?: readonly string[];
  chips?: readonly string[];
  /** 主角图:dept 卡是 512×341 双人横图,外链舱是 384×384 单人方图(`solo` 换构图锁)。 */
  figure?: { url: string; solo?: boolean };
  /** 卡脚左侧:dept 卡是积分标(价目表现算),外链舱是计费口径说明。 */
  footNote: ReactNode;
  footCta: string;
  footCtaIcon: LucideIcon;
  /**
   * 主推档 —— 同一组里**只给一张**。不新增装饰层,只把已有的 .wb-accent / .wb-mesh
   * 各加深一档(理由见 index.css 的 .wb-card--feature)。
   */
  feature?: boolean;
  /** 整卡可点时的可读名(外链舱必须在这句里说清「会新开标签页」)。 */
  ariaLabel?: string;
  onOpen: () => void;
}

export function WorkbenchCabinShell({
  cabinKey,
  legacyAttrName,
  index,
  chart,
  icon: Icon,
  title,
  tagline,
  desc,
  minis,
  chips,
  figure,
  footNote,
  footCta,
  footCtaIcon: CtaIcon,
  feature,
  ariaLabel,
  onOpen,
}: CabinShellProps) {
  const style = {
    "--ac": identityVar(chart),
    "--i": String(index),
  } as CSSProperties;

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onOpen}
      onKeyDown={onKeyDown}
      data-cabin-card={cabinKey}
      {...(legacyAttrName ? { [legacyAttrName]: cabinKey } : {})}
      className={cn("wb-card", feature && "wb-card--feature")}
      style={style}
    >
      <span className="wb-accent" aria-hidden />
      <span className="wb-mesh" aria-hidden />
      <span className="wb-glow" aria-hidden />
      {/* 主角图:靠右、向左渐隐融进卡底。文案由 [data-cabin-card] > .wb-body 的
          padding-right 让位,★不靠"应该不会重叠"侥幸 —— chips 会换行,重叠必然发生。 */}
      {figure && (
        <span
          className={cn("wb-fig", figure.solo && "wb-fig--solo")}
          style={{ backgroundImage: `url(${figure.url})` }}
          aria-hidden
        />
      )}
      <div className="wb-body">
        <div className="wb-body-main">
          <div className="flex items-center gap-3">
            <div className="wb-tile">
              <Icon className="h-[22px] w-[22px]" strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[15px] font-semibold tracking-tight">{title}</div>
              <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                {tagline}
              </span>
            </div>
          </div>

          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-muted-foreground">{desc}</p>

          {minis && minis.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {minis.map((m) => (
                <span
                  key={m}
                  className="rounded-md border border-border/60 bg-background/50 px-1.5 py-0.5 text-2xs font-medium text-foreground/80"
                >
                  {m}
                </span>
              ))}
            </div>
          )}

          {chips && chips.length > 0 && (
            <div className="wb-chips">
              {chips.map((c) => (
                <span key={c} className="wb-chip">
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="wb-foot">
          <span className="mr-auto text-2xs font-normal text-muted-foreground">{footNote}</span>
          {footCta}
          <CtaIcon className="wb-foot-ic" />
        </div>
      </div>
    </div>
  );
}
